import { Controller } from "@hotwired/stimulus"

// Full-screen email preview opened from chat cards. Attach to a container;
// any child with data-email-preview-url opens the modal on click.
export default class extends Controller {
    connect() {
        this._onClick = (e) => {
            let trigger = e.target.closest("[data-email-preview-url]")
            if (!trigger || !this.element.contains(trigger)) return
            e.preventDefault()
            e.stopPropagation()
            this._open(trigger.dataset.emailPreviewUrl, trigger)
        }
        this.element.addEventListener("click", this._onClick)
    }

    disconnect() {
        this.element.removeEventListener("click", this._onClick)
        this._close()
    }

    // Public API so sibling controllers (thread-triage) can drive the modal.
    open(url, trigger = null) {
        this._open(url, trigger)
    }

    close() {
        this._close()
    }

    isOpen() {
        return !!this._overlay
    }

    _open(url, trigger = null) {
        this._close()

        let overlay = document.createElement("div")
        overlay.className = "email-modal-overlay"
        overlay.innerHTML = `
            <div class="email-modal" role="dialog" aria-modal="true">
                <button type="button" class="email-modal-close" aria-label="Close">
                    <i class="bi bi-x-lg"></i>
                </button>
                <div class="email-modal-content">
                    <p class="email-modal-loading">Loading email…</p>
                </div>
            </div>`

        overlay.addEventListener("click", (e) => {
            if (e.target === overlay || e.target.closest(".email-modal-close")) this._close()
        })
        this._onKeydown = (e) => {
            if (e.key === "Escape") this._close()
        }
        document.addEventListener("keydown", this._onKeydown)

        document.body.appendChild(overlay)
        document.body.classList.add("email-modal-open")
        this._overlay = overlay
        this.dispatch("opened", { detail: { url: url, trigger: trigger } })

        fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((html) => {
                let content = overlay.querySelector(".email-modal-content")
                if (!content) return
                content.innerHTML = html
                this._autosizeFrame(content.querySelector("iframe[data-email-frame]"))
            })
            .catch(() => {
                let content = overlay.querySelector(".email-modal-content")
                if (content) content.innerHTML = '<p class="email-modal-loading">Could not load this email.</p>'
            })
    }

    // Grow the sandboxed srcdoc iframe to fit the email. Sandbox includes
    // allow-same-origin (not allow-scripts) so we can read scrollHeight without
    // letting email content run JavaScript.
    _autosizeFrame(frame) {
        if (!frame) return
        let resize = () => {
            try {
                let doc = frame.contentDocument
                if (!doc || !doc.documentElement) return
                let height = Math.max(doc.documentElement.scrollHeight, doc.body ? doc.body.scrollHeight : 0)
                if (height > 0) frame.style.height = `${height + 24}px`
            } catch {
                // Cross-origin access denied — keep the CSS fallback height.
            }
        }
        frame.addEventListener("load", () => {
            resize()
            // Remote images load after the document does; re-measure a few times.
            setTimeout(resize, 300)
            setTimeout(resize, 1200)
        })
        resize()
    }

    _close() {
        if (this._onKeydown) document.removeEventListener("keydown", this._onKeydown)
        this._onKeydown = null
        let wasOpen = !!this._overlay
        if (this._overlay) {
            this._overlay.remove()
            this._overlay = null
        }
        document.body.classList.remove("email-modal-open")
        if (wasOpen) this.dispatch("closed")
    }
}
